import { createContext, useContext, useState, useEffect, useCallback } from "react";

export interface CartItem {
  id: number;
  nameAr: string;
  price: number;
  image: string;
  category: string;
  qty: number;
  stock?: number; // المخزون المتاح
}

interface CartContextType {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  isOpen: boolean;
  addItem: (item: Omit<CartItem, "qty">, qty?: number) => void;
  removeItem: (id: number) => void;
  updateQty: (id: number, qty: number) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextType | null>(null);

const STORAGE_KEY = "al_ebdaa_cart";

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = useCallback((item: Omit<CartItem, "qty">, qty = 1) => {
    // منع إضافة منتج نفد مخزونه
    if (item.stock !== undefined && item.stock <= 0) return;

    setItems((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        const maxQty = item.stock ?? existing.stock ?? 999;
        const newQty = Math.min(existing.qty + qty, maxQty);
        return prev.map((i) =>
          i.id === item.id ? { ...i, qty: newQty, stock: item.stock ?? i.stock } : i
        );
      }
      const maxQty = item.stock ?? 999;
      return [...prev, { ...item, qty: Math.min(qty, maxQty) }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((id: number) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQty = useCallback((id: number, qty: number) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
    } else {
      setItems((prev) =>
        prev.map((i) => {
          if (i.id !== id) return i;
          // لا تتجاوز المخزون المتاح
          const maxQty = i.stock ?? 999;
          return { ...i, qty: Math.min(qty, maxQty) };
        })
      );
    }
  }, []);

  const clearCart = useCallback(() => setItems([]), []);
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const totalItems = items.reduce((sum, i) => sum + i.qty, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.qty, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        totalItems,
        totalPrice,
        isOpen,
        addItem,
        removeItem,
        updateQty,
        clearCart,
        openCart,
        closeCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
